import React, { useEffect, useState } from "react";
import { qformat, DATE_FORMATS, formatRelativeVerbose } from "views_shared/app/javascript/utils/dateFormatters";
import LoadingState from "../shared/LoadingState";
import ErrorState from "../shared/ErrorState";

// Card view (for overview page)

function ActivityCardView({ userData, userSlug }) {
  return (
    <div className="card mb-4 border-secondary" style={{ borderLeftWidth: "4px" }}>
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <h5 className="card-title">
              <i className="fa fa-clock text-secondary me-2" />
              Activity
            </h5>
            <div className="row mt-3">
              <div className="col-auto">
                <small className="text-muted d-block">Member Since</small>
                <span className="fs-5 fw-semibold">{qformat(userData?.created_at, DATE_FORMATS.date)}</span>
                <br />
                <small className="text-muted">{formatRelativeVerbose(userData?.created_at)}</small>
              </div>
              <div className="col-auto">
                <small className="text-muted d-block">Last Sign In</small>
                <span className="fs-5 fw-semibold">
                  {qformat(userData?.last_sign_in_at, DATE_FORMATS.date, "Never")}
                </span>
                {userData?.last_sign_in_at && (
                  <>
                    <br />
                    <small className="text-muted">{formatRelativeVerbose(userData.last_sign_in_at)}</small>
                  </>
                )}
              </div>
            </div>
          </div>
          <a href={`/admin/users/${userSlug}/activity`} className="btn btn-outline-primary btn-sm">
            Details <i className="fa fa-arrow-right ms-1" />
          </a>
        </div>
      </div>
    </div>
  );
}

// Full view components

function ActivityOverviewSection({ userData, userProfile }) {
  return (
    <div className="card mb-4">
      <div className="card-header">
        <h5 className="mb-0">
          <i className="fa fa-clock me-2" />
          Account Activity
        </h5>
      </div>
      <div className="card-body">
        <table className="table table-sm">
          <tbody>
            <tr>
              <td>
                <strong>Member Since</strong>
              </td>
              <td>
                {qformat(userData?.created_at, DATE_FORMATS.date)}
                <small className="text-muted ms-2">({formatRelativeVerbose(userData?.created_at)})</small>
              </td>
            </tr>
            <tr>
              <td>
                <strong>Last Sign In</strong>
              </td>
              <td>
                {userData?.last_sign_in_at ? (
                  <>
                    {qformat(userData.last_sign_in_at, DATE_FORMATS.date)}
                    <small className="text-muted ms-2">({formatRelativeVerbose(userData.last_sign_in_at)})</small>
                  </>
                ) : (
                  <span className="text-muted">Never</span>
                )}
              </td>
            </tr>
            <tr>
              <td>
                <strong>Sign In Count</strong>
              </td>
              <td>{userData?.sign_in_count || 0}</td>
            </tr>
            <tr>
              <td>
                <strong>Current Sign In At</strong>
              </td>
              <td>
                {userData?.current_sign_in_at ? (
                  <>
                    {qformat(userData.current_sign_in_at, DATE_FORMATS.date)}
                    <small className="text-muted ms-2">({formatRelativeVerbose(userData.current_sign_in_at)})</small>
                  </>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </td>
            </tr>
            <tr>
              <td>
                <strong>Profile Updated</strong>
              </td>
              <td>
                {userProfile?.updated_at ? (
                  <>
                    {qformat(userProfile.updated_at, DATE_FORMATS.date)}
                    <small className="text-muted ms-2">({formatRelativeVerbose(userProfile.updated_at)})</small>
                  </>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ActivityFullView({ userData, userProfile }) {
  return (
    <div className="admin-user-activity">
      <ActivityOverviewSection userData={userData} userProfile={userProfile} />
      {/* Future: Add more activity sections like login history, profile changes, etc. */}
    </div>
  );
}

/**
 * AdminUserActivity - Activity component for admin user detail page
 *
 * Supports two modes:
 * - mode="card": Compact summary card with link to full view (for overview page)
 * - mode="full": Complete activity details
 *
 * @param {string} userId - User ID to fetch data for
 * @param {string} userSlug - User slug for generating links (required for card mode)
 * @param {string} mode - "card" or "full" (default: "full")
 */
function AdminUserActivity({ userId, userSlug, mode = "full" }) {
  const [userData, setUserData] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isCardMode = mode === "card";

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { supabaseClient } = globalThis.Qwoted;

        const [userResult, profileResult] = await Promise.all([
          supabaseClient.from("users").select().eq("id", userId).single(),
          supabaseClient.from("user_profiles").select().eq("slug", userSlug).single(),
        ]);

        if (userResult.error) {
          throw new Error(userResult.error.message);
        }

        if (profileResult.error) {
          throw new Error(profileResult.error.message);
        }

        setUserData(userResult.data);
        setUserProfile(profileResult.data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, userSlug]);

  if (loading) return <LoadingState compact={isCardMode} label="Loading activity data..." />;
  if (error) return <ErrorState message={error} title="Unable to Load Activity Data" compact={isCardMode} />;

  if (isCardMode) {
    return <ActivityCardView userData={userData} userSlug={userSlug} />;
  }

  return <ActivityFullView userData={userData} userProfile={userProfile} />;
}

export default AdminUserActivity;
